// ClientProfileService.tsx
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ClientProfileService } from '../../core/services';
import { PageSize } from '../../type';
import { GetCustomerIntegrationsResponse } from './interface';

export const useGetClientsService = (initialParams: PageSize, typeName: string) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-clients-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getClients(
          {
            page: initialParams.pageNumber ?? pageParam,
            size: initialParams.size,
            name: initialParams.name,
            userName: initialParams.userName,
            accountTitle: initialParams.accountTitle,
            contractNumber: initialParams.contractNumber,
            type: initialParams.type,
            source: initialParams.source,
          },
          typeName
        );
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetClientsDropdownService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-clients-dropdown-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getClientsDropdown({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          search: initialParams.search,
          type: initialParams.type,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetCustomersService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-customers-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getCustomers({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          name: initialParams.name,
          userName: initialParams.userName,
          phone: initialParams.phone,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetDriversService = (initialParams: PageSize, accountId: number) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-drivers-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getDrivers(
          {
            page: initialParams.pageNumber ?? pageParam,
            size: initialParams.size,
            type: initialParams.type,
            name: initialParams.name,
            active: initialParams.active,
          },
          accountId
        );
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetAccountsDropdownService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-accounts-dropdown-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getAccounts({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          search: initialParams.search,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetHostingDetailsService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-hosting-details-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getHostingDetails({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          title: initialParams.title,
          userName: initialParams.userName,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      // retry: 1,
      // retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetReviewsService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-reviews-data'],
      queryFn: async ({ pageParam }) => {
        return ClientProfileService.getReviews({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          userId: initialParams.userId,
          clientId: initialParams.clientId,
          accountId: initialParams.accountId,
          rating: initialParams.rating,
          startDate: initialParams.startDate,
          endDate: initialParams.endDate,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetCustomerIntegrationService = (businessId: number) => {
  const { data, isLoading, isRefetching, error, refetch } = useQuery<
    GetCustomerIntegrationsResponse,
    unknown
  >({
    queryKey: ['customer-integration-data', businessId],
    queryFn: async () => {
      return ClientProfileService.getCustomerIntegrations(businessId);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
};
